import { Country } from '@/helpers/conf';
import { MediaType } from '@/helpers/enums';
import type { IHeadlinePromoPageConfig } from '@/modules/promotions/templates/HeadlinePromoTemplate/helpers';

const IMAGE_PATH = '/headline-promo/odyssey';

const getPromoImg = (path: string) => `${IMAGE_PATH}/${path}.webp`;

const BG_STAGE_0 = {
  desk: getPromoImg('bg-desk-1'),
  mob: getPromoImg('bg-mob-1'),
};
const BG_STAGE_1 = {
  desk: getPromoImg('bg-desk-2'),
  mob: getPromoImg('bg-mob-2'),
};
const BG_STAGE_2 = {
  desk: getPromoImg('bg-desk-3'),
  mob: getPromoImg('bg-mob-3'),
};
const BG_STAGE_3 = {
  desk: getPromoImg('bg-desk-4'),
  mob: getPromoImg('bg-mob-4'),
};

const GLOW_COLOR = '37, 170, 214';
const GLOW_END_OPACITY = 0.8;

const CHARACTER_IMAGE = {
  desk: getPromoImg('main-character'),
  mob: getPromoImg('main-character'),
};

export const ODYSSEY_CONFIG: IHeadlinePromoPageConfig = {
  promoKey: 'odyssey',
  route: '/headline-promo',
  namespace: 'odyssey-promo',
  disallowedCountries: [Country.Germany],
  useFullNameAsGroupId: true,
  disableAnimations: true,
  progressBarVariant: 'pill',
  dateFormat: 'MMM dd',
  hideHowToDescription: false,
  faqBgImage: getPromoImg('faq-bg-desk'),
  levelsMobileBreakpoint: MediaType.MOBILE_S,

  cardHud: {
    kind: 'svg',
    viewBox: '0 0 120.228 38.2',
    fillPath: 'M119.628 2.83534H2.57222V37.6H103.85L119.628 19.7177V2.83534Z',
    strokePath:
      'M0.6 18.4823V0.6L35.1139 0.6M2.57222 2.83534H119.628V19.7177L103.85 37.6H2.57222V2.83534Z',
  },

  levelsConfig: [
    {
      name: 'First Light',
      image: getPromoImg('level_1'),
      lockedImage: getPromoImg('level_1'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 0, 25: 6, 50: 12, 75: 18, 100: 25 },
      reward: { kind: 'freespins', count: 20 },
      rewardGroupKey: 'odyssey_l1_reward',
      bg: BG_STAGE_0,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Bush Runner',
      image: getPromoImg('level_2'),
      lockedImage: getPromoImg('level_2'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 25, 25: 31, 50: 37, 75: 43, 100: 50 },
      reward: { kind: 'freespins', count: 30 },
      rewardGroupKey: 'odyssey_l2_reward',
      bg: BG_STAGE_0,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Coral Voyager',
      image: getPromoImg('level_3'),
      lockedImage: getPromoImg('level_3'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 50, 25: 62, 50: 75, 75: 87, 100: 100 },
      reward: { kind: 'freespins', count: 40 },
      rewardGroupKey: 'odyssey_l3_reward',
      bg: BG_STAGE_0,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Reef Explorer',
      image: getPromoImg('level_4'),
      lockedImage: getPromoImg('level_4'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 100, 25: 125, 50: 150, 75: 175, 100: 200 },
      reward: { kind: 'freespins', count: 50 },
      rewardGroupKey: 'odyssey_l4_reward',
      bg: BG_STAGE_1,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Red Dune Rider',
      image: getPromoImg('level_5'),
      lockedImage: getPromoImg('level_5'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 200, 25: 250, 50: 300, 75: 350, 100: 400 },
      reward: { kind: 'freespins', count: 60 },
      rewardGroupKey: 'odyssey_l5_reward',
      bg: BG_STAGE_1,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Outback Blaze',
      image: getPromoImg('level_6'),
      lockedImage: getPromoImg('level_6'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 400, 25: 487, 50: 575, 75: 662, 100: 750 },
      reward: { kind: 'freespins', count: 70 },
      rewardGroupKey: 'odyssey_l6_reward',
      bg: BG_STAGE_1,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Uluru Orbit',
      image: getPromoImg('level_7'),
      lockedImage: getPromoImg('level_7'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 750, 25: 937, 50: 1125, 75: 1312, 100: 1500 },
      reward: { kind: 'freespins', count: 80 },
      rewardGroupKey: 'odyssey_l7_reward',
      bg: BG_STAGE_2,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
      artOverflows: false,
    },
    {
      name: 'Southern Cross',
      image: getPromoImg('level_8'),
      lockedImage: getPromoImg('level_8'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 1500, 25: 1875, 50: 2250, 75: 2625, 100: 3000 },
      reward: { kind: 'bonus', amount: 10 },
      rewardGroupKey: 'odyssey_l8_reward',
      bg: BG_STAGE_2,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
      artOverflows: false,
    },
    {
      name: 'Starlight Voyager',
      image: getPromoImg('level_9'),
      lockedImage: getPromoImg('level_9'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 3000, 25: 3500, 50: 4000, 75: 4500, 100: 5000 },
      reward: { kind: 'freespins', count: 60 },
      rewardGroupKey: 'odyssey_l9_reward',
      bg: BG_STAGE_2,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
      artOverflows: false,
    },
    {
      name: 'Solar Frontier',
      image: getPromoImg('level_10'),
      lockedImage: getPromoImg('level_10'),
      promoRewardType: 'bets',
      wagerAmounts: { 0: 5000, 25: 6250, 50: 7500, 75: 8750, 100: 10_000 },
      reward: { kind: 'bonus', amount: 15 },
      rewardGroupKey: 'odyssey_l10_reward',
      bg: BG_STAGE_3,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
      artOverflows: false,
    },
    {
      name: 'Outback Legend',
      image: getPromoImg('level_11'),
      lockedImage: getPromoImg('level_11'),
      promoRewardType: 'bets',
      wagerAmounts: {
        0: 10_000,
        25: 12_500,
        50: 15_000,
        75: 17_500,
        100: 20_000,
      },
      reward: { kind: 'bonus', amount: 20 },
      rewardGroupKey: 'odyssey_l11_reward',
      bg: BG_STAGE_3,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
      artOverflows: false,
    },
    {
      name: 'Solar Champion',
      image: getPromoImg('level_12'),
      lockedImage: getPromoImg('level_12'),
      promoRewardType: 'bets',
      wagerAmounts: {
        0: 20_000,
        25: 22_500,
        50: 25_000,
        75: 27_500,
        100: 30_000,
      },
      reward: { kind: 'bonus', amount: 30 },
      rewardGroupKey: 'odyssey_l12_reward',
      bg: BG_STAGE_3,
      glowColor: GLOW_COLOR,
      glowEndOpacity: GLOW_END_OPACITY,
      characterImage: CHARACTER_IMAGE,
      artOverflows: false,
    },
  ],
};
